/**
 * EscalationForm — modal-less form to open an escalation against a
 * department. Used from the department detail page (Escalations tab)
 * and from the recent-activity timeline.
 */
import {
  type Escalation,
  type EscalationRequest,
  escalationRequestSchema,
} from '@chaos/types/department';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { ApiError } from '../../lib/api/client';
import { departmentsApi, departmentsKeys } from '../../lib/api/departments';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';

interface EscalationFormProps {
  departmentKey: string;
  trigger: React.ReactNode;
  onSubmitted?: (escalation: Escalation) => void;
}

export function EscalationForm({ departmentKey, trigger, onSubmitted }: EscalationFormProps) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  const form = useForm<EscalationRequest>({
    resolver: zodResolver(escalationRequestSchema),
    defaultValues: {
      departmentKey,
      severity: 'warn',
      title: '',
      description: '',
      suggestedOwnerRole: null,
      payload: {},
    },
  });

  const mutation = useMutation({
    mutationFn: (input: EscalationRequest) => departmentsApi.openEscalation(input),
    onSuccess: (escalation) => {
      qc.invalidateQueries({ queryKey: departmentsKeys.list() });
      qc.invalidateQueries({ queryKey: departmentsKeys.detail(departmentKey) });
      setOpen(false);
      form.reset({
        departmentKey,
        severity: 'warn',
        title: '',
        description: '',
        suggestedOwnerRole: null,
        payload: {},
      });
      onSubmitted?.(escalation);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Unable to open the escalation.';
      form.setError('root', { message: msg });
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    mutation.mutate(values);
  });

  const rootError = form.formState.errors.root?.message;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Open an escalation</DialogTitle>
          <DialogDescription>
            Flag something that needs human attention. The escalation is recorded against this
            department's audit log.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-3 pt-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="escalation-severity" className="text-[12px]">
                Severity
              </Label>
              <Select
                value={form.watch('severity')}
                onValueChange={(v) =>
                  form.setValue('severity', v as EscalationRequest['severity'], {
                    shouldValidate: true,
                  })
                }
              >
                <SelectTrigger id="escalation-severity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warn">Warning</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="escalation-owner" className="text-[12px]">
                Suggested owner (optional)
              </Label>
              <Input
                id="escalation-owner"
                placeholder="e.g. founder"
                {...form.register('suggestedOwnerRole')}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="escalation-title" className="text-[12px]">
              Title
            </Label>
            <Input
              id="escalation-title"
              {...form.register('title')}
              placeholder="Vendor onboarding stuck for 3 days"
              autoFocus
            />
            {form.formState.errors.title ? (
              <span className="text-[11px] text-destructive">
                {form.formState.errors.title.message}
              </span>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="escalation-description" className="text-[12px]">
              Description (optional)
            </Label>
            <Textarea
              id="escalation-description"
              {...form.register('description')}
              rows={3}
              placeholder="Context, links, screenshots…"
            />
          </div>

          {rootError ? <p className="text-[12px] text-destructive">{rootError}</p> : null}

          <DialogFooter className="mt-2 gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={mutation.isPending || !form.formState.isValid}
            >
              {mutation.isPending ? 'Opening…' : 'Open escalation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
